import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import ClaimListingDialog from '../ModalDialog/Ownership/ClaimListingModalDialog';
import Button from '../Button';
import { COLORS, POL_THEME, ThemeContext } from '../../../constants/theme';
import customerDataRequest from '../../../services/api/customer-data';
import styles from './OwnershipRequest.scss';
import { LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR } from '../../../constants/site';
import { getAuthToken } from '../../../auth/authHelpers';

const OwnershipRequest = ({ listingId, listingTitle, listingType }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const themeContext = useContext(ThemeContext);
    const isParishTheme = themeContext === POL_THEME;
    const isOrganization = listingType === LISTING_TYPE_ORGANIZATION;

    const {
        getAccessTokenSilently,
        user,
        isAuthenticated,
    } = useAuth0();

    const createOwnershipRequest = async () => {
        setDialogOpen(false);
        const token = await getAuthToken(getAccessTokenSilently);
        const baseUri = isOrganization ? '/organizations' : '/sponsors';
        const bodyData = { emailAddress: user?.email };

        await customerDataRequest('post', `${baseUri}/${listingId}/ownership-request`, token, bodyData)
            .then(() => {
                setDialogOpen(false);
                toast.success('Your request was successful. '
                    + 'Please check your e-mail to continue with your ownership request.');
            })

            .catch(() => {
                setDialogOpen(false);
                toast.error('There was an error attempting to send the ownership request. '
                    + 'Please try again in a few minutes');
            });
    };

    const getTitle = () => {
        if (isAuthenticated) {
            return 'Register as staff';
        }
        return 'You must be logged in to register as staff';
    };

    return (
        <div className={styles.container}>
            {isOrganization ? (
                <div className={styles.section}>
                    <div className={styles.content}>
                        {`${isParishTheme ? 'Parish staff' : 'Staff'}`}
                        {' '}
                        are eligible to modify this information
                    </div>
                    <Button variant="contained" onClick={() => setDialogOpen(true)}>
                        Register as
                        {` ${isParishTheme ? 'Parish Staff' : 'Staff'}`}
                    </Button>
                </div>
            ) : (
                <div className={styles.claimBusinessContainer}>
                    <Button onClick={() => setDialogOpen(true)} color={COLORS.flat}>
                        Do you own this business?
                        <div>Claim this listing to edit this page</div>
                    </Button>
                </div>
            )}

            <ClaimListingDialog
                handleCancel={() => setDialogOpen(false)}
                open={dialogOpen}
                title={getTitle()}
                handleClaim={createOwnershipRequest}
                content={`Would you like to manage the listing information for ${listingTitle}?
                You will be contacted via email within 48 business hours if you are approved.`}
                listingId={listingId}
                listingType={listingType}
            />
        </div>
    );
};

OwnershipRequest.propTypes = {
    listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listingTitle: PropTypes.string,
    listingType: PropTypes.oneOf([LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR]),
};

OwnershipRequest.defaultProps = {
    listingId: '',
    listingTitle: '',
    listingType: LISTING_TYPE_ORGANIZATION,
};

export default OwnershipRequest;
